import { Link, useLocation } from "react-router-dom";
import { Home, Landmark, Store, CalendarDays, UserCircle, Compass, Heart, MessageSquareMore } from "lucide-react";
import { getAppType } from "@/hooks/useCapacitorApp";
import { cn } from "@/lib/utils";

export const BottomNavigation = () => {
  const location = useLocation();
  const appType = getAppType();
  
  const appNavItems = [
    { id: "home", to: "/couple/dashboard", label: "Home", icon: Home },
    { id: "venues", to: "/directory?type=venue", label: "Venues", icon: Landmark },
    { id: "suppliers", to: "/directory?type=suppliers", label: "Suppliers", icon: Store },
    { id: "planner", to: "/couple/planner", label: "Planner", icon: CalendarDays },
    { id: "profile", to: "/couple/profile", label: "Profile", icon: UserCircle },
  ];
  const webNavItems = [
    { id: "web-home", to: "/", label: "Home", icon: Home },
    { id: "web-explore", to: "/directory", label: "Explore", icon: Compass },
    { id: "web-shortlist", to: "/couple/shortlist", label: "Shortlist", icon: Heart },
    { id: "web-messages", to: "/messages", label: "Messages", icon: MessageSquareMore },
    { id: "web-planner", to: "/couple/planner", label: "Planning", icon: CalendarDays },
  ];
  const navItems = appType === "couple" ? appNavItems : webNavItems;

  if (appType === "pro") return null;

  const isActive = (id: string) => {
    const params = new URLSearchParams(location.search);
    const directoryType = params.get("type");
    if (id === "web-home") return location.pathname === "/";
    if (id === "web-explore") return location.pathname.startsWith("/directory");
    if (id === "web-shortlist") return location.pathname.startsWith("/couple/shortlist");
    if (id === "web-messages") return location.pathname.startsWith("/messages");
    if (id === "web-planner") return location.pathname.startsWith("/couple/planner");
    if (id === "home") return location.pathname === "/couple/dashboard" || location.pathname === "/couple/explore";
    if (id === "venues") return location.pathname === "/directory" && (directoryType === "venue" || directoryType === "venues");
    if (id === "suppliers") return location.pathname === "/directory" && directoryType === "suppliers";
    if (id === "planner") return location.pathname === "/couple/planner" || [
      "/couple/build", "/couple/budget", "/couple/guests", "/couple/checklist",
      "/couple/website", "/couple/timeline", "/couple/seating", "/couple/seating-chart",
      "/couple/moodboard", "/couple/notes", "/couple/events", "/couple/weather"
    ].some((path) => location.pathname.startsWith(path));
    if (id === "profile") return location.pathname === "/couple/profile" || location.pathname === "/couple/settings" || location.pathname === "/couple/co-planners";
    return false;
  };

  return (
    <nav 
      className={cn(
        "eal-bottom-navigation fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur-xl",
        appType === "couple" ? "eal-app-tabbar" : "md:hidden"
      )}
      style={{ 
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)',
      }}
    >
      <div className="grid h-[4.5rem] grid-cols-5 px-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.id);
          return (
            <Link
              key={item.id}
              to={item.to}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
              className={cn(
                "group relative flex min-w-0 flex-col items-center justify-center gap-1 transition-colors touch-manipulation",
                active ? "text-primary" : "text-muted-foreground"
              )}
            >
              <span className={cn("absolute inset-x-4 top-0 h-0.5 bg-primary transition-opacity", active ? "opacity-100" : "opacity-0")} />
              <div className={cn(
                "flex h-8 w-11 items-center justify-center rounded-full transition-all duration-200",
                active ? "bg-primary/10" : "group-active:scale-95 group-active:bg-muted"
              )}>
                <Icon 
                  className="h-[19px] w-[19px]"
                  strokeWidth={active ? 2.25 : 1.75}
                />
              </div>
              <span className={cn("max-w-full truncate px-0.5 text-[10px] leading-none", active ? "font-semibold" : "font-medium")}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
